import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'
import Button from '@vtex/styleguide/lib/Button'
import Alert from '@vtex/styleguide/lib/Alert'
import Input from '@vtex/styleguide/lib/Input'
import Check from '@vtex/styleguide/lib/icon/Check'

import { ELECTRON_PLATFORM_TYPE } from '@/checkout-instore-legacy/constants/DeviceConstants'
import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'
import { getChannel } from '@/checkout-instore-legacy/utils/device/communication'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader'

import ListPrinters from './ListPrinters'

class Device extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      deviceName: '',
      appKey: '',
      appToken: '',
      defaultPrinter: null,
    }
  }

  updateStateFromProps() {
    const { device, credentials } = this.props

    this.setState({
      deviceName: device && device.deviceName,
      defaultPrinter: device && device.defaultPrinter,
      ...credentials,
    })
  }

  componentDidMount() {
    this.updateStateFromProps()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.device !== this.props.device ||
      prevProps.credentials !== this.props.credentials
    ) {
      this.updateStateFromProps()
    }
  }

  handleDeviceNameChange = (e) => {
    this.setState({ deviceName: e.target.value })
  }

  handleAppKeyChange = (e) => {
    this.setState({ appKey: e.target.value })
  }

  handleAppTokenChange = (e) => {
    this.setState({ appToken: e.target.value })
  }

  handleSubmit = (e) => {
    e.preventDefault()

    const { deviceName, appKey, appToken, defaultPrinter } = this.state

    const { deviceId } = this.props.device

    this.handleBlur()

    this.props.updateDevice(
      {
        deviceId,
        deviceName: deviceName || '',
        defaultPrinter,
      },
      { appKey, appToken }
    )
  }

  handleBlur = () => {
    const { deviceName, appKey, appToken } = this.state

    const { deviceId } = this.props.device

    if (appKey && appToken) {
      WebViewBridge.sendEvent({
        type: 'configureBroker',
        data: {
          device: { deviceName, deviceId },
          channel: getChannel(),
          credentials: { appKey, appToken },
        },
      })
      this.props.setCredentials({ appKey, appToken })
    }
  }

  render() {
    const {
      intl,
      appInfo: { platform },
    } = this.props
    const { success, error: credentialsError } = this.props.credentials || {}

    if (!this.props.device) return null

    const { deviceName, appKey, appToken } = this.state

    const { loading, error } = this.props.device

    const iconSuffix = success ? (
      <div className={'c-success flex justify-center'}>
        <Check />
      </div>
    ) : null

    const credentialsErrorMessageId = credentialsError
      ? credentialsError.status === 401
        ? 'unauthorizedErrorMessage'
        : 'credentialsErrorMessage'
      : undefined
    const credentialsErrorMessage = credentialsErrorMessageId
      ? intl.formatMessage({
          id: credentialsErrorMessageId,
        })
      : ''
    return (
      <React.Fragment>
        <Loader loading={loading} />
        {error && (
          <Alert type="warning">
            <FormattedMessage id="configureDeviceError" />
          </Alert>
        )}

        <div className="pt6">
          <div className="tc mb6">
            <div className="flex justify-center">
              <div className="f2-ns f3 tc fw7 tc pt6-l pb0">
                <FormattedMessage id="printNetworkConnect" />
              </div>
            </div>
          </div>
          <form
            className="form-horizontal text-center pa5 pt0-l"
            onSubmit={this.handleSubmit}
            data-testid="configure-device-form"
          >
            <div className="pt1">
              {platform === ELECTRON_PLATFORM_TYPE && (
                <React.Fragment>
                  <div className="w-100 w-30-l w-80-ns mt6 center">
                    <Input
                      label={intl.formatMessage({
                        id: 'appKey',
                      })}
                      id="app-key"
                      className="w-100 w-80-ns w-30-l pa5 ba b--black-20 f4-ns lh-copy-ns lh-solid-l swiper-no-swiping"
                      type="text"
                      placeholder={intl.formatMessage({
                        id: 'insertAppKey',
                      })}
                      onChange={this.handleAppKeyChange}
                      onBlur={this.handleBlur}
                      value={appKey}
                      error={!!credentialsError}
                      suffix={iconSuffix}
                    />
                  </div>
                  <div className="w-100 w-30-l w-80-ns mt6 center">
                    <Input
                      label={intl.formatMessage({
                        id: 'appToken',
                      })}
                      id="app-token"
                      className="w-100 w-80-ns w-30-l pa5 ba b--black-20 f4-ns lh-copy-ns lh-solid-l swiper-no-swiping"
                      type="text"
                      placeholder={intl.formatMessage({
                        id: 'insertAppToken',
                      })}
                      onChange={this.handleAppTokenChange}
                      onBlur={this.handleBlur}
                      value={appToken}
                      error={!!credentialsError}
                      errorMessage={credentialsErrorMessage}
                      suffix={iconSuffix}
                    />
                  </div>
                </React.Fragment>
              )}
              <div className="w-100 w-30-l w-80-ns mt6 center">
                <Input
                  label={intl.formatMessage({
                    id: 'insertDeviceName',
                  })}
                  id="device-name"
                  className="w-100 w-80-ns w-30-l pa5 ba b--black-20 f4-ns lh-copy-ns lh-solid-l swiper-no-swiping"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'insertDeviceName',
                  })}
                  onChange={this.handleDeviceNameChange}
                  value={deviceName}
                />
              </div>
              <div className="w-100 w-30-l w-80-ns mt6 pb3 center">
                <ListPrinters
                  device={this.props.device}
                  onSelectPrinter={(value) =>
                    this.setState({ defaultPrinter: value })
                  }
                  intl={intl}
                  credentials={this.props.credentials}
                />
              </div>
              <div className="w-100 w-30-l w-80-ns mt7 center">
                <Button
                  id="configure-device-submit"
                  variation="primary"
                  size="large"
                  type="submit"
                  disabled={
                    deviceName === '' ||
                    (platform === ELECTRON_PLATFORM_TYPE &&
                      (appKey === '' || appToken === ''))
                  }
                  block
                >
                  {intl.formatMessage({
                    id: 'configureDeviceConfirmationButton',
                  })}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </React.Fragment>
    )
  }
}

Device.propTypes = {
  intl: intlShape.isRequired,
  device: PropTypes.shape({
    deviceId: PropTypes.string,
    deviceName: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.bool,
  }),
  credentials: PropTypes.shape({
    appKey: PropTypes.string,
    appToken: PropTypes.string,
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.object,
  }),
  appInfo: PropTypes.shape({
    platform: PropTypes.string,
  }).isRequired,
  updateDevice: PropTypes.func.isRequired,
  setCredentials: PropTypes.func.isRequired,
}

export default injectIntl(Device)
